/**
 * DocViewer — visualizador do documento na ficha da visita (US-08 AC4/AC6), como o do
 * protótipo (`bindViewer`/`.vbox` em `docs/prototype/index.html`; spec
 * `ajustes-login-e-visualizador`, Req 2):
 *  - caixa de altura fixa com rolagem; a imagem abre **na largura da caixa** (ajustada);
 *  - um clique na imagem ajustada amplia 2,5× centralizando o ponto clicado;
 *  - `+`/`−` mudam o zoom em 0,75× (até 5×; abaixo de 1× volta ao ajuste), mantendo o centro;
 *  - "Ajustar" volta à largura da caixa e ao topo;
 *  - ampliada, arrastar move a imagem (rolagem da caixa); ajustada, a roda rola a página.
 * Usa a `viewerUrl` pré-assinada; sem ela, o placeholder (AC6). Nenhum dado pessoal vai a
 * analytics/console (RN-M11-001).
 */

import { useEffect, useLayoutEffect, useRef, useState, MouseEvent, PointerEvent as ReactPointerEvent } from 'react';

/** `null` = ajustada (largura da caixa); `n` = n × a largura da caixa. */
export type Zoom = number | null;

export const ZOOM_CLIQUE = 2.5;
export const ZOOM_PASSO = 0.75;
export const ZOOM_MAX = 5;

export function aumentar(z: Zoom): number {
  return Math.min(ZOOM_MAX, (z ?? 1) + ZOOM_PASSO);
}

export function diminuir(z: Zoom): Zoom {
  if (z === null) {
    return null;
  }
  const proximo = z - ZOOM_PASSO;
  return proximo <= 1 ? null : proximo;
}

export function larguraDaImagem(z: Zoom): string {
  return z === null ? '100%' : `${z * 100}%`;
}

/** Fração (0..1) da imagem que deve ficar no centro da caixa depois do próximo render. */
interface Foco {
  readonly fx: number;
  readonly fy: number;
}

function fracao(valor: number, total: number): number {
  if (!(total > 0)) {
    return 0.5;
  }
  return Math.min(1, Math.max(0, valor / total));
}

/** `alt`: o que a imagem é (padrão, o documento da visita; a evidência complementar aberta diz o tipo — ADR-048). */
export function DocViewer({ viewerUrl, alt = 'Documento da visita' }: { viewerUrl: string | null; alt?: string }): JSX.Element {
  const [zoom, setZoom] = useState<Zoom>(null);
  const [arrastando, setArrastando] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const imagem = useRef<HTMLImageElement>(null);
  const foco = useRef<Foco | null>(null);
  const arraste = useRef<{ x: number; y: number; l: number; t: number } | null>(null);

  // Outra imagem (outra ficha): volta ao ajuste e ao topo.
  useEffect(() => {
    setZoom(null);
    foco.current = null;
    if (caixa.current) {
      caixa.current.scrollLeft = 0;
      caixa.current.scrollTop = 0;
    }
  }, [viewerUrl]);

  // Depois de aplicar o zoom, centraliza o ponto guardado (clique ou centro da vista).
  useLayoutEffect(() => {
    const c = caixa.current;
    const img = imagem.current;
    const f = foco.current;
    foco.current = null;
    if (!c || !img || !f || zoom === null) {
      return;
    }
    c.scrollLeft = f.fx * img.offsetWidth - c.clientWidth / 2;
    c.scrollTop = f.fy * img.offsetHeight - c.clientHeight / 2;
  }, [zoom]);

  /** Guarda o ponto da imagem que está no centro da vista, para mantê-lo ao mudar o zoom. */
  const guardarCentro = (): void => {
    const c = caixa.current;
    const img = imagem.current;
    if (c && img) {
      foco.current = {
        fx: fracao(c.scrollLeft + c.clientWidth / 2, img.offsetWidth),
        fy: fracao(c.scrollTop + c.clientHeight / 2, img.offsetHeight),
      };
    }
  };

  const mais = (): void => {
    guardarCentro();
    setZoom((z) => aumentar(z));
  };
  const menos = (): void => {
    guardarCentro();
    setZoom((z) => diminuir(z));
  };
  const ajustar = (): void => {
    foco.current = null;
    setZoom(null);
    if (caixa.current) {
      caixa.current.scrollLeft = 0;
      caixa.current.scrollTop = 0;
    }
  };

  const onClick = (e: MouseEvent<HTMLDivElement>): void => {
    if (zoom !== null || !imagem.current) {
      return;
    }
    const r = imagem.current.getBoundingClientRect();
    foco.current = { fx: fracao(e.clientX - r.left, r.width), fy: fracao(e.clientY - r.top, r.height) };
    setZoom(ZOOM_CLIQUE);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const c = caixa.current;
    if (zoom === null || !c) {
      return;
    }
    arraste.current = { x: e.clientX, y: e.clientY, l: c.scrollLeft, t: c.scrollTop };
    if (typeof c.setPointerCapture === 'function') {
      try {
        c.setPointerCapture(e.pointerId);
      } catch {
        // Ponteiro já liberado (ex.: toque cancelado): o arraste segue sem a captura.
      }
    }
    setArrastando(true);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const a = arraste.current;
    const c = caixa.current;
    if (!a || !c) {
      return;
    }
    c.scrollLeft = a.l - (e.clientX - a.x);
    c.scrollTop = a.t - (e.clientY - a.y);
  };
  const encerrarArraste = (): void => {
    arraste.current = null;
    setArrastando(false);
  };

  if (!viewerUrl) {
    return (
      <div className="viewer">
        <div className="viewer-placeholder">
          Visualização indisponível no momento. O caminho e o download do original permanecem
          disponíveis abaixo.
        </div>
      </div>
    );
  }

  return (
    <div className="viewer">
      <div
        ref={caixa}
        className={`viewer-box${zoom !== null ? ' zoomed' : ''}${arrastando ? ' dragging' : ''}`}
        data-testid="viewer-box"
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={encerrarArraste}
        onPointerCancel={encerrarArraste}
        onPointerLeave={encerrarArraste}
      >
        <img
          ref={imagem}
          src={viewerUrl}
          alt={alt}
          draggable={false}
          style={{ width: larguraDaImagem(zoom) }}
        />
      </div>
      <div className="viewer-controls">
        <button type="button" aria-label="Diminuir zoom" onClick={menos}>
          −
        </button>
        <button type="button" aria-label="Ajustar" onClick={ajustar}>
          Ajustar
        </button>
        <button type="button" aria-label="Aumentar zoom" onClick={mais}>
          +
        </button>
      </div>
    </div>
  );
}
